import { Away, CharacterState, characterStateOf, durationMs, resolveColour } from "odyssey-shared";
import { ABOVE_HEAD, LINE } from "./layout.ts";

/**
 * The idle and AFK mark over a nameplate (ADR-090 decision 8 and its amendment of 2026-10-03; the author's design of
 * 2026-09-25, `IdleMark` and the Nameplate's `away`, a visual reference): three pixel z's rising left to right, muted
 * for idle and in the text colour with the word AFK beside them for AFK, looping over `idle-loop` in four steps (z1;
 * z1 and z2; all three; none), all three still under reduced motion. Another player's stands over a plate that draws;
 * the viewer's own stands over its character with its plate drawn or not, where the plate's top would be. Pure: the
 * nameplates surface draws what these functions say.
 *
 * The same box holds a frozen character's mark (ADR-056's amendment "a frozen character is safe", decision 45): for
 * the away state `Held` the hourglass, still, in the info colour and with no word. The away state is one field, so
 * the z's and the hourglass never both show. It follows neither the viewer's Show idle and AFK marks choice nor
 * whether the character is alive: it tells others why a character does not answer and cannot be struck.
 */

/**
 * The z's box in logical pixels, as the design draws it; the atlas frame adds a 1 px ink outline on every side.
 */
export const IDLE_MARK = { width: 16, height: 12, outline: 1 } as const;

/**
 * The gap between the z's and the word AFK, and between the mark and the plate's top, in logical pixels.
 */
export const IDLE_MARK_GAP = 2;

/**
 * The loop's steps; the last draws nothing.
 */
const LOOP_STEPS = 4;

/**
 * The step reduced motion holds: all three z's.
 */
const STILL_STEP = 3;

/**
 * The atlas frames of the loop's steps that draw, made once so a frame reads a name rather than builds one.
 */
const IDLE_FRAMES: readonly string[] = Object.freeze(["ui/world/idle-1_16", "ui/world/idle-2_16", "ui/world/idle-3_16"]);
const AFK_FRAMES: readonly string[] = Object.freeze(["ui/world/afk-1_16", "ui/world/afk-2_16", "ui/world/afk-3_16"]);

/**
 * Where a mark's parts stand, in logical pixels: the z's frame's top-left (its outline included) and the word's
 * top-left.
 */
export interface IdleMarkLayout {
  zX: number;
  zY: number;
  wordX: number;
  wordY: number;
}

/**
 * A fresh layout for `idleMarkLayout` to fill.
 *
 * @returns The layout, zeroed.
 */
export function newIdleMarkLayout(): IdleMarkLayout {
  return { zX: 0, zY: 0, wordX: 0, wordY: 0 };
}

/**
 * Whether a character's mark draws: idle or AFK, alive, the viewer's choice showing marks, and either over a plate
 * that draws or over the viewer's own character, whose mark needs no plate (ADR-090's amendment of 2026-10-03).
 *
 * @param away The character's away state.
 * @param anim Its animation bits, whose state says whether it is dead.
 * @param own Whether it is the viewer's own character.
 * @param plate Whether the character's plate draws this frame.
 * @param show The viewer's Show idle and AFK marks choice.
 * @returns True when the mark draws.
 */
export function idleMarkShown(away: number, anim: number, own: boolean, plate: boolean, show: boolean): boolean {
  return show && (own || plate) && (away === Away.Idle || away === Away.Afk) && characterStateOf(anim) !== CharacterState.Dead;
}

/**
 * The loop's step at a time: 1 to 4, 12 frames of `idle-loop` each, every mark in step; 3 under reduced motion.
 *
 * @param timeMs The scene's clock in ms.
 * @param reduced Whether reduced motion holds.
 * @returns The step.
 */
export function idleLoopStep(timeMs: number, reduced: boolean): number {
  if (reduced) {
    return STILL_STEP;
  }
  const loop = durationMs("idle-loop");
  const into = ((timeMs % loop) + loop) % loop;
  return Math.floor((into * LOOP_STEPS) / loop) + 1;
}

/**
 * The atlas frame a mark draws at a step, or null for the blank step.
 *
 * @param away Idle or AFK.
 * @param step The loop's step, 1 to 4.
 * @returns The frame's name.
 */
export function idleMarkFrame(away: number, step: number): string | null {
  if (step < 1 || step >= LOOP_STEPS) {
    return null;
  }
  return (away === Away.Afk ? AFK_FRAMES : IDLE_FRAMES)[step - 1] ?? null;
}

/**
 * Lays a mark out over a plate: centred on the plate's anchor, the z's and, for AFK, the word 2 px right of them on
 * their bottom line, the whole 2 px over the plate's top, on whole device pixels.
 *
 * @param anchorX The plate's anchor across, in device pixels.
 * @param anchorY The plate's anchor down, in device pixels.
 * @param scale The UI scale.
 * @param wordWidth The word AFK's width in logical pixels, 0 for idle.
 * @param out The layout to fill.
 * @returns `out`.
 */
export function idleMarkLayout(anchorX: number, anchorY: number, scale: number, wordWidth: number, out: IdleMarkLayout): IdleMarkLayout {
  const width = wordWidth > 0 ? IDLE_MARK.width + IDLE_MARK_GAP + wordWidth : IDLE_MARK.width;
  const left = anchorX - Math.floor((width * scale) / 2);
  const plateTop = anchorY - (ABOVE_HEAD + LINE.height) * scale;
  const bottom = plateTop - IDLE_MARK_GAP * scale;
  out.zX = left / scale - IDLE_MARK.outline;
  out.zY = bottom / scale - IDLE_MARK.height - IDLE_MARK.outline;
  out.wordX = left / scale + IDLE_MARK.width + IDLE_MARK_GAP;
  out.wordY = bottom / scale - LINE.height;
  return out;
}

/**
 * A frozen character's mark: the hourglass glyph of the UI atlas, 8 logical pixels square with no outline of its own.
 */
export const HELD_MARK = { size: 8, frame: "ui/glyph/hourglass_8" } as const;

/**
 * The info colour as a tint, a frozen character's mark's.
 */
const INFO_TINT = Number.parseInt(resolveColour("info").slice(1), 16);

/**
 * No tint: an atlas frame in its own colours.
 */
const NO_TINT = 0xffffff;

/**
 * Whether a frozen character's mark draws: the away state is held, and either its plate draws or it is the viewer's
 * own character, whose mark needs no plate. A concealed character has no plate, so another player's hides with it.
 *
 * @param away The character's away state.
 * @param own Whether it is the viewer's own character.
 * @param plate Whether the character's plate draws this frame.
 * @returns True when the mark draws.
 */
export function heldMarkShown(away: number, own: boolean, plate: boolean): boolean {
  return away === Away.Held && (own || plate);
}

/**
 * Whether the box over a plate draws anything: the idle and AFK mark (`idleMarkShown`) or a frozen character's
 * (`heldMarkShown`).
 *
 * @param away The character's away state.
 * @param anim Its animation bits.
 * @param own Whether it is the viewer's own character.
 * @param plate Whether the character's plate draws this frame.
 * @param show The viewer's Show idle and AFK marks choice, which the frozen mark does not follow.
 * @returns True when a mark draws.
 */
export function awayMarkShown(away: number, anim: number, own: boolean, plate: boolean, show: boolean): boolean {
  return heldMarkShown(away, own, plate) || idleMarkShown(away, anim, own, plate, show);
}

/**
 * The atlas frame the box draws at a step: the hourglass at every step for a frozen character, the loop's frame for
 * idle and AFK.
 *
 * @param away The away state, idle, AFK or held.
 * @param step The loop's step, 1 to 4.
 * @returns The frame's name, or null for the z's blank step.
 */
export function awayMarkFrame(away: number, step: number): string | null {
  return away === Away.Held ? HELD_MARK.frame : idleMarkFrame(away, step);
}

/**
 * The tint the box's image takes: the info colour for a frozen character's hourglass, none for the z's, which are
 * drawn in their own colours.
 *
 * @param away The away state.
 * @returns The tint.
 */
export function awayMarkTint(away: number): number {
  return away === Away.Held ? INFO_TINT : NO_TINT;
}

/**
 * Lays a frozen character's mark out where the idle mark stands: centred on the plate's anchor, its bottom 2 px over
 * the plate's top, on whole device pixels. The word's place is the mark's own, unused.
 *
 * @param anchorX The plate's anchor across, in device pixels.
 * @param anchorY The plate's anchor down, in device pixels.
 * @param scale The UI scale.
 * @param out The layout to fill.
 * @returns `out`.
 */
export function heldMarkLayout(anchorX: number, anchorY: number, scale: number, out: IdleMarkLayout): IdleMarkLayout {
  const left = anchorX - Math.floor((HELD_MARK.size * scale) / 2);
  const bottom = anchorY - (ABOVE_HEAD + LINE.height) * scale - IDLE_MARK_GAP * scale;
  out.zX = left / scale;
  out.zY = bottom / scale - HELD_MARK.size;
  out.wordX = out.zX;
  out.wordY = out.zY;
  return out;
}
